import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import OutputText from '@ey/canvascoreservices/OutputText';

import {labels, currentResource} from '../../../../../../util/utils';
import {clearErrors, raiseJSValidationError} from '../../../../../../actions/erroractions';
import {updateContactsAccess} from '../../../../../../actions/ContactDBManageAccess';
import {updateDisplayData} from '../../../../../../actions/Metrics/dashboardContextAction';
import {
	userIsNotEntityAdminOfAllSelectedEntitiesErrorCode,
	newUserIsNotEntityAdminOfAllSelectedEntitiesErrorCode,
	projectSessionKeys,
	userLevelAccess
} from '../../../../../../util/uiconstants';

import {
	ACCESS_TO_CONTACT_LIST,
	ACCESS_TO_DASHBOARD,
	DASHBOARD_VIEWS,
	ENTITIES,
	HAS_EXTERNAL,
	USER_LIST,
	ACCESS_TO_GROUP_RISK_ASSESMENT
} from './utils/constants';
import {
	PROJECT_OWNER_ACCESS,
	ENTITY_ADMIN_ACCESS,
	PROJECT_OWNER,
	ENTITY_ADMIN,
	USER
} from '../../AddContact/ACForm/utils/constants';
import getInitialValues from './utils/getInitialValues';
import normalizePayload from './utils/normalizePayload';
import normalizeForUpdateDisplayData from './utils/normalizeForUpdateDisplayData';

import useForm from '../../../../../CoreWrappers/Form/useForm';
import Button from '../../../../../CoreWrappers/Button';
import HrAlt from '../../../../../Common/Hr/HrAlt';
import LoadingIndicator from '../../../../../Common/LoadingIndicator';
import AccessDefinePanel from '../../AddContact/ACForm/AccessDefinePanel';
import StyledMAForm from './StyledMAForm';
import LoadingWrapper from '../../../../../CoreWrappers/LoadingWrapper';

const MAForm = () => {
	const [isLoading, setIsLoading] = useState(false);

	const {selectedContacts, displayedEntities} = useSelector(({contacts}) => ({
		selectedContacts: contacts.selected,
		displayedEntities: contacts.displayedEntities
	}));
	const orgChart = useSelector((store) => store.dashboardContext?.orgChart);
	const userGui = useSelector((state) => state.userPreferences?.gui);
	const projectOwners = useSelector(({project}) => project?.users?.filter(({isProjectOwner}) => isProjectOwner) || []);
	const project = useSelector((store) => store.project);
	const contactEntities = useSelector((state) => state.contacts.entities);
	const engagements = useSelector(({dashboardContext}) => dashboardContext?.engagements);
	const userPreferences = useSelector((store) => store.userPreferences);
	const contacts = useSelector(({contacts}) => contacts);
	const engagementMetadata = useSelector((store) => store?.engagementMetaData);

	const userType = userPreferences?.userType;

	const isProjectOwner = project.users?.find(({userId}) => userId === userGui)?.isProjectOwner;
	const isGroupRiskAssessment = project.isGroupRiskAssessmentProject;

	const projectSessionKey = `${projectSessionKeys.projectdetail}_${project.projectId}`;

	const selectedIsEntityAdmin = contacts?.entities
		?.map((m) => m.entityAdmins)
		?.flat()
		?.map((m) => m.userId)
		?.some((s) => contacts.selected?.includes(s));
	const selectedIsProjectOwner =
		contacts?.selected?.length === projectOwners.filter((owner) => contacts?.selected?.includes(owner.userId)).length;

	const constants = {
		USER_LIST,
		HAS_EXTERNAL,
		ENTITIES,
		DASHBOARD_VIEWS,
		ACCESS_TO_GROUP_RISK_ASSESMENT,
		ACCESS_TO_CONTACT_LIST,
		ACCESS_TO_DASHBOARD,
		PROJECT_OWNER_ACCESS,
		ENTITY_ADMIN_ACCESS,
		PROJECT_OWNER,
		ENTITY_ADMIN,
		USER
	};

	const getInitialValuesArgs = {
		entities: displayedEntities,
		selected: selectedContacts,
		isProjectOwner,
		orgChart,
		userGui,
		constants,
		selectedIsEntityAdmin: selectedIsEntityAdmin,
		selectedIsProjectOwner: selectedIsProjectOwner
	};

	const dispatch = useDispatch();

	const clearErrorsAndDispatch = (dispatchCB) => dispatch(clearErrors()).then(() => dispatch(dispatchCB));

	const dispatchNoEntitySelectedToast = () => {
		clearErrorsAndDispatch(raiseJSValidationError(labels.mustSelectOneEntity));
	};

	const dispatchNoViewsSelectedToast = () => {
		clearErrorsAndDispatch(raiseJSValidationError(labels.mustSelectOneView));
	};

	const dispatchNoAccessEditedForProjectOwners = (selectedOwners) => {
		const message = `${labels.cannotEditAccessForProjectOwners.replace(
			'{list}',
			selectedOwners
				.map(({name, lastName}) => `${name} ${lastName}`)
				.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
				.join(', ')
		)}`;
		clearErrorsAndDispatch(raiseJSValidationError(message));
	};

	const initialValues = {...getInitialValues(getInitialValuesArgs)};
	const schema = {
		[USER_LIST]: {required: false},
		[HAS_EXTERNAL]: {required: false},
		[ENTITIES]: {required: false},
		[ACCESS_TO_GROUP_RISK_ASSESMENT]: {required: false},
		[DASHBOARD_VIEWS]: {required: false},
		[ACCESS_TO_CONTACT_LIST]: {required: false},
		[ACCESS_TO_DASHBOARD]: {required: false},
		[PROJECT_OWNER_ACCESS]: {required: false},
		[ENTITY_ADMIN_ACCESS]: {required: false}
	};

	const displayCustomError = (error, users) => {
		setIsLoading(false);
		const errorCode = error?.response?.data?.errors?.Error[0]?.split('-')[0].trim() || '';
		if (errorCode == userIsNotEntityAdminOfAllSelectedEntitiesErrorCode) {
			const resouceLabel = currentResource.Errors[errorCode];
			let userList = [];
			users?.forEach((user) => userList.push(`${user.name} ${user.lastName}`));
			userList = userList.sort();
			let names = '';
			userList?.forEach((user, i) => {
				if (i === userList.length - 1) {
					names = names + user;
				} else {
					names = names + user + ', ';
				}
			});
			dispatch(
				raiseJSValidationError(
					`<strong>${newUserIsNotEntityAdminOfAllSelectedEntitiesErrorCode}:</strong>` +
						' ' +
						resouceLabel.replace('{0}', names)
				)
			);
		}
	};

	const removeDuplicates = (arr) => {
		return arr.filter((item, index) => arr.indexOf(item) === index);
	};

	const dispatchExternalUserDomainNotAllowed = (entities) => {
		dispatch(raiseJSValidationError(`${labels.externalUserDomainMismatch}<br>${labels.entitiesLabels}:${entities}`));
	};

	const {values, errors, handleChange, handleSubmit, handleReset, isFormValid} = useForm({
		initialValues,
		schema,
		onSubmit: (values, callback) => {
			let valid = true;
			const entitiesList = values[ENTITIES];
			if (!values[ENTITIES].length > 0 && valid) {
				valid = false;
				dispatchNoEntitySelectedToast();
			}

			if (
				values[ACCESS_TO_DASHBOARD] &&
				!values[DASHBOARD_VIEWS].some(({checked, indeterminate}) => checked && !indeterminate) &&
				valid
			) {
				valid = false;
				dispatchNoViewsSelectedToast();
			}

			const selectedOwners = values[USER_LIST].filter(({userId}) =>
				projectOwners.some((owner) => owner.userId === userId && userId === userGui)
			);

			if (selectedOwners.length > 0 && valid) {
				valid = false;
				dispatchNoAccessEditedForProjectOwners(selectedOwners);
			}
			let seletedEntities = contactEntities.filter((x) => values[ENTITIES].some((y) => y.entityId === x.entityId));

			let entityContacts = [];
			seletedEntities?.map((entity) => {
				let isEntityAdmins = entity?.entityAdmins?.some((x) => x.userId == userPreferences.userId);

				if (!isEntityAdmins) {
					let contacts = entity?.contacts;
					entityContacts = [...entityContacts, ...contacts];
				}
			});

			let contactsSelected = removeDuplicates(values[USER_LIST]);

			let users = [];
			contactsSelected?.map((contactId) => {
				let contact = entityContacts.find((x) => x.userId == contactId.userId);
				if (contact != undefined) {
					users = [...users, contact];
				}
			});

			// Check external domains not allowed.
			let entitiesWithValidDomain = [];
			let entitiesWithInvalidDomain = {ids: [], labels: []};

			contactsSelected?.forEach((us) => {
				if (!us?.isInternal) {
					const userDomain = `@${us?.emailAddress?.split('@')[1]}`;

					entitiesList.forEach((ent) => {
						let isUserDomainInEntity = false;
						const entityDomains = project?.orgStructure?.find((en) => en?.entityId === ent?.entityId)?.domains;

						if (!!ent.checked && !!!ent.indeterminate) {
							if (entityDomains?.length > 0) {
								entityDomains?.forEach((d) => {
									let domainPattern = d.replace('@', '\\@');
									domainPattern = domainPattern.replaceAll('*', '\\w+');
									domainPattern = domainPattern.replaceAll('.', '\\.*');

									const regexPattern = new RegExp(domainPattern);

									if (regexPattern.test(userDomain)) {
										isUserDomainInEntity = true;
										entitiesWithValidDomain?.push(ent);
									}
								});
							}

							if (!isUserDomainInEntity) {
								if (!entitiesWithInvalidDomain?.ids?.includes(ent.entityId)) {
									entitiesWithInvalidDomain?.ids?.push(ent.entityId);
									entitiesWithInvalidDomain?.labels?.push(` ${ent.label}`);
								}
							}
						}
					});

					if (entitiesWithInvalidDomain?.ids?.length > 0) {
						dispatchExternalUserDomainNotAllowed(entitiesWithInvalidDomain?.labels);
						valid = false;
						setIsLoading(false);
					}
				}
			});

			const apiPayload = normalizePayload(initialValues, values, constants);
			if (valid && apiPayload) {
				let {entities = null, ...apiData} = apiPayload;
				const apiCalls = [];
				let newEntities = [...contactEntities];
				entities
					.sort((a, b) => (a.operation === b.operation ? 0 : a.operation ? -1 : 1))
					.forEach((entity) => {
						const body = {
							...apiData,
							entityOperation: entity.operation,
							entityId: entity.entityId,
							userLevelAccess: userLevelAccess in entity ? entity.userLevelAccess : apiData.userLevelAccess
						};
						apiCalls.push(() =>
							dispatch(updateContactsAccess(project.projectId, entity.entityId, body))
								.then(() => {
									newEntities = normalizeForUpdateDisplayData({
										users: values[USER_LIST],
										entityList: newEntities,
										entityData: entity,
										viewData: apiData.views,
										clAccess: apiData.hasContactListAccess,
										graAccess: apiData.hasGroupRiskAssessmentAccess,
										userLevelAccess: apiData.userLevelAccess
									});
									dispatch(updateDisplayData(engagements, newEntities, orgChart, true, body));
									callback();
								})
								.catch((error) => displayCustomError(error, users))
						);
					});

				const sequentionallyDispatchApiArray = async () => {
					// This array of API calls needs to be executed sequentionally otherwise it fails (cos' CosmoDB)
					setIsLoading(true);
					const results = [];
					for (const call of apiCalls) results.push(await Promise.allSettled([call()]));
					return results.flat();
				};
				sequentionallyDispatchApiArray()
					.then(() => {
						setIsLoading(false);
					})
					.catch(() => {
						setIsLoading(false);
					});
			}

			if (!apiPayload) callback();
		}
	});

	const renderTitle = () => <OutputText>{labels.manageAccess}</OutputText>;

	const renderControls = () => {
		const saveProps = {
			label: labels.save,
			onClick: () =>
				handleSubmit(() => {
					handleReset();
				})
		};

		const cancelProps = {
			variant: 'ghost',
			label: labels.cancelLabel
		};

		return (
			<>
				<HrAlt />
				<div className="controls">
					<Button {...cancelProps} />
					<Button {...saveProps} />
				</div>
			</>
		);
	};

	if (selectedContacts.length !== values[USER_LIST].length) {
		const {[USER_LIST]: userList, ...rest} = getInitialValues(getInitialValuesArgs);
		handleChange(USER_LIST, userList, rest);
	}

	const sharedProps = {
		values,
		errors,
		handleChange,
		handleSubmit,
		handleReset,
		isFormValid,
		isLoading
	};

	return (
		<StyledMAForm className="customScrollbar" isLoading={isLoading}>
			{renderTitle()}
			<AccessDefinePanel {...sharedProps} />
			{renderControls()}
		</StyledMAForm>
	);
};

MAForm.propTypes = {
	open: PropTypes.bool.isRequired
};

MAForm.defaultProps = {};

export default MAForm;
